import * as React from 'react'

import {
  FlagDropdown,
  TextEditor,
  ConceptSelector,
  LightButtonLoadingSpinner,
  ALPHA_FLAG,
} from '../../index'

interface OptimalResponseObjInterface {
  text: string;
  optimal: boolean;
  count: number;
  feedback: string;
  concept_results: { conceptUID: string, correct: boolean }[];
}

interface QuestionInterface {
  prompt: string;
  conceptUID?: string; // passed in for diagnostic questions
  conceptID?: string; // passed in for connect questions
  instructions: string;
  flag: string;
  cuesLabel: string;
  cues: string[];
}

interface GrammarQuestionInterface {
  prompt: string;
  concept_uid: string;
  instructions: string;
  flag: string;
  rule_description: string;
  answers: { text: string, }[];
  cues: string[];
  cues_label: string;
}

interface QuestionFormPropsInterface {
  submit: (questionObj: QuestionInterface|GrammarQuestionInterface, optimalResponseObj?: OptimalResponseObjInterface) => void;
  question: QuestionInterface|GrammarQuestionInterface;
  concepts: { hasreceiveddata: boolean};
  isNew?: boolean;
  defaultConceptUID?: string;
  defaultInstructions: string[];
  isGrammarQuestion?: boolean;
}

// note that defaultInstructions related code is currently only used in Connect

export const QuestionForm = ({ question, defaultConceptUID, submit, isNew, concepts, defaultInstructions, isGrammarQuestion, }: QuestionFormPropsInterface) => {
  const [prompt, setPrompt] = React.useState(question.prompt || '')
  const [ruleDescription, setRuleDescription] = React.useState(question.rule_description || '') // only used in grammar
  const [conceptUID, setConceptUID] = React.useState(question.conceptUID || question.conceptID || question.concept_uid || defaultConceptUID)
  const [instructions, setInstructions] = React.useState(question.instructions)
  const [flag, setFlag] = React.useState(question.flag || ALPHA_FLAG)
  const [cuesLabel, setCuesLabel] = React.useState(question.cuesLabel || question.cues_label || '')
  const [cues, setCues] = React.useState(question.cues?.join(',') || '')
  const [optimalResponseText, setOptimalResponseText] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)
  const [showDefaultInstructions, setShowDefaultInstructions] = React.useState(false)
  const [showConceptNullError, setShowConceptNullError] = React.useState(false)

  function handleClickSubmit() {
    if (!conceptUID) {
      setShowConceptNullError(true)
      return
    }

    setSubmitting(true)

    if (isGrammarQuestion) {
      submit({
        prompt,
        concept_uid: conceptUID,
        instructions,
        flag,
        rule_description: ruleDescription,
        answers: [{ text: optimalResponseText, }],
        cues: cues.split(',').filter(cue => cue.length),
        cues_label: cuesLabel
      })
    } else {
      const { focusPoints, incorrectSequences, modelConceptUID, } = question

      const questionObj = {
        conceptUID,
        focusPoints,
        incorrectSequences,
        modelConceptUID,
        prompt,
        cues: cues.split(','),
        instructions,
        flag,
        cuesLabel
      }

      if (isNew) {
        const optimalResponseObj = {
          text: optimalResponseText.trim(),
          optimal: true,
          count: 0,
          feedback: "That's a strong sentence!",
          concept_results: [{ conceptUID, correct: true }]
        }
        submit(questionObj, optimalResponseObj)
      } else {
        submit(questionObj)
      }
    }
  }

  const handleOptimalResponseText = (e) => {
    setOptimalResponseText(e.target.value);
  };

  const handlePromptChange = (e) => {
    setPrompt(e)
  };

  const handleRuleDescriptionChange = (e) => {
    setRuleDescription(e)
  };

  const handleInstructionsChange = (e) => {
    setInstructions(e.target.value)

    if (defaultInstructions) {
      if (e.target.value === '/') {
        setShowDefaultInstructions(true)
      } else {
        setShowDefaultInstructions(false)      }
    }
  };

  const renderOptimalResponse = () => {
    if (!isNew) { return }

    return (
      <div>
        <label className="label" htmlFor="optimalResponse" id="optimalResponseLabel">Optimal Response</label>
        <p className="control">
          <input aria-labelledby="optimalResponseLabel" className="input" id="optimalResponse" onChange={handleOptimalResponseText} type="text" />
        </p>
      </div>
    )
  };

  const handleSelectorChange = (e) => {
    setConceptUID(e.value)
  };

  const handleFlagChange = (e) => {
    setFlag(e.target.value)
  };

  const handleCuesLabelChange = (e) => {
    setCuesLabel(e.target.value)
  };

  const handleCuesChange = (e) => {
    setCues(e.target.value)
  };

  const renderButtonText = () => {
    if (submitting) {
      return <LightButtonLoadingSpinner />
    }
    return isNew ? 'Submit Question' : 'Update Question'
  }

  const renderDefaultInstructions = () => {
    if (!defaultInstructions || !showDefaultInstructions) { return }
    const defaultInstructionsDiv = defaultInstructions.map((item, i) =>
      (<button
        className="default"
        key={i}
        onClick={handleInstructionsChange}
        type="button"
        value={item}
      >
        {item}
      </button>)
    )

    return <div className='default-instructions'>{defaultInstructionsDiv}</div>
  };

  const renderRuleDescription = () =>  {
    if (!isGrammarQuestion) { return }

    return (
      <React.Fragment>
        <label className="label" htmlFor="ruleDescription">Rule description (optional, will overwrite the concept's description for this question if set)</label>
        <p className="control">
          <TextEditor
            handleTextChange={handleRuleDescriptionChange}
            id="ruleDescription"
            text={ruleDescription}
          />
        </p>
      </React.Fragment>
    )
  }

  const renderConceptSelector = () => {
    const labelClass = showConceptNullError ? 'red-label' : ''

    return (
      <div>
        <label className={`label ${labelClass}`} htmlFor="concept" id="conceptLabel">Concept</label>
        <div>
          <ConceptSelector
            currentConceptUID={conceptUID}
            handleSelectorChange={handleSelectorChange}
            id="concept"
          />
          {showConceptNullError && <p className={labelClass}>Add a concept to save this question</p>}
        </div>
      </div>
    )
  }

  if(concepts.hasreceiveddata) {
    return (
      <div className="box">
        <h6 className="control subtitle">{isNew ? 'Create a new question' : 'Edit question'}</h6>
        <label className="label" htmlFor="prompt" id="promptLabel">Prompt</label>
        <TextEditor
          aria-labelledby="promptLabel"
          handleTextChange={handlePromptChange}
          id="prompt"
          text={prompt}
        />
        <label className="label" htmlFor="instructions" id="instructionsLabel">Instructions for student</label>
        <p className="control">
          <textarea aria-labelledby="instructionsLabel" className="input" id="instructions" onChange={handleInstructionsChange} value={instructions} />
        </p>
        {renderDefaultInstructions()}
        <label className="label" htmlFor="cuesLabel" id="cuesLabelLabel">Cues Label (default is {isGrammarQuestion ? '“choose one“': '“joining words“/“joining word“ for single cues'}, enter a space to have no label)</label>
        <p className="control">
          <input aria-labelledby="cuesLabelLabel" className="input" defaultValue={cuesLabel} id="cuesLabel" onChange={handleCuesLabelChange} type="text" />
        </p>
        <label className="label" htmlFor="cuesString" id="cuesStringLabel">Cues (separated by commas, no spaces eg “however,therefore,hence“)</label>
        <p className="control">
          <input aria-labelledby="cuesStringLabel" className="input" defaultValue={cues} id="cuesString" onChange={handleCuesChange} type="text" />
        </p>
        {renderRuleDescription()}
        {renderOptimalResponse()}
        <FlagDropdown flag={flag} handleFlagChange={handleFlagChange} isLessons={false} />
        {renderConceptSelector()}
        <br />
        <button className="quill-button contained medium green focus-on-light" onClick={handleClickSubmit} type="button">{renderButtonText()}</button>
      </div>
    )
  } else {
    return (<div>Loading...</div>)
  }
}
